'use client';

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import Link from '@/components/public/i18n/LocalizedLink';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import { Arrow, UniverseIcon } from './Icons';
import './ecosystem.css';

/** The light follows measured connectors; Élina is visibly inside the R-Node nucleus. */
export default function EcosystemMap({ paused = false, onSelect }: { paused?: boolean; onSelect?: (index: number) => void }) {
  const { t } = useI18n();
  const root = useRef<HTMLDivElement>(null);
  const captionId = useId();
  const [paths, setPaths] = useState<string[]>([]);
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    let frame = 0;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      const bounds = element.getBoundingClientRect();
      const box = (selector: string) => element.querySelector(selector)?.getBoundingClientRect();
      const sx = box('.eco-method'), core = box('.eco-core'), rise = box('.eco-interface');
      if (!sx || !core || !rise) return;
      const sxX = sx.left + sx.width / 2 - bounds.left, sxY = sx.bottom - bounds.top;
      const coreX = core.left + core.width / 2 - bounds.left, coreY = core.top - bounds.top;
      const result = [`M ${sxX} ${sxY} C ${sxX} ${sxY + 16} ${coreX} ${coreY - 16} ${coreX} ${coreY}`];
      if (rise.top > core.top + core.height / 2) {
        const bottom = core.bottom - bounds.top, top = rise.top - bounds.top;
        const riseX = rise.left + rise.width / 2 - bounds.left;
        result.push(`M ${coreX} ${bottom} C ${coreX} ${bottom + 16} ${riseX} ${top - 16} ${riseX} ${top}`);
      } else {
        const left = core.right - bounds.left, right = rise.left - bounds.left;
        const y = core.top + core.height / 2 - bounds.top, ry = rise.top + rise.height / 2 - bounds.top;
        result.push(`M ${left} ${y} C ${left + 16} ${y} ${right - 16} ${ry} ${right} ${ry}`);
      }
      setPaths(result);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    element.querySelectorAll('[data-eco-node]').forEach(node => observer.observe(node));
    document.fonts.ready.then(schedule);
    schedule();
    return () => { disposed = true; cancelAnimationFrame(frame); observer.disconnect(); };
  }, []);
  const select = (index: number) => ({
    onFocus: () => onSelect?.(index),
    onPointerEnter: (event: React.PointerEvent) => { if (event.pointerType === 'mouse') onSelect?.(index); },
  });
  return <figure className={`eco-figure${paused ? ' is-paused' : ''}`} aria-describedby={captionId}>
    <div className="eco-map" ref={root}>
      <svg className="eco-connectors" aria-hidden="true" focusable="false">
        {paths.map((path, index) => <g key={index}>
          <path d={path} />
          <circle className="eco-light" r="3" style={{ offsetPath: `path('${path}')`, animationDelay: `${index * -2}s` } as CSSProperties} />
        </g>)}
      </svg>
      <div className="eco-origin"><span className="eco-origin-dot" />{t('Votre expertise, votre vision, votre ambition')}</div>
      <Link className="eco-method" href="/methode-sx" data-eco-node {...select(2)}>
        <span className="eco-node-icon"><UniverseIcon id="sx-business" /></span>
        <span><strong>{t('Méthode SX')}</strong><small>{t('Traduire votre intelligence collective')}</small></span><Arrow diagonal />
      </Link>
      <div className="eco-core" data-eco-node {...select(1)}>
        <Link className="eco-core-heading" href="/r-node"><span className="eco-node-icon"><UniverseIcon id="r-node" /></span><span><small>{t('Le noyau métier')}</small><strong>R-Node</strong></span><Arrow diagonal /></Link>
        <p>{t('Vos savoir-faire, vos règles et vos opérations réunis.')}</p>
        <Link className="eco-elina" href="/elina" {...select(3)}>
          <img src="/assets/universes/elina-chat-cutout.webp" alt="" width="42" height="52" />
          <span><strong>{t('Élina intégrée')}</strong><small>{t('Commerciaux · Intervenants · Direction')}</small></span><Arrow diagonal />
        </Link>
      </div>
      <Link className="eco-interface" href="/rise" data-eco-node {...select(0)}>
        <span className="eco-node-icon"><UniverseIcon id="rise" /></span>
        <small>{t('L’interface avec vos publics')}</small><strong>RISE</strong>
        <p>{t('Portail public, site interactif ou showroom partenaires.')}</p>
        <span className="eco-publics">{t('Prospects · Clients · Partenaires')}<Arrow diagonal /></span>
      </Link>
    </div>
    <figcaption id={captionId}>{t('SX guide la conception. R-Node organise l’activité, avec l’assistance d’Élina. RISE relie ce noyau à vos publics.')}</figcaption>
  </figure>;
}
